import React, { useState } from 'react';
import { ArrowRight } from './icons';
import { SlotMachineButton } from './SlotMachineButton';
import { useContactForm } from '../hooks/useContactForm';
import { useGA4 } from '../hooks/useGA4';
import { getApiBaseUrl } from '../utils/imageUrl';

interface FormData {
  fullName: string;
  email: string;
  message: string;
}

export function ContactForm(): React.JSX.Element {
  const { data: formData, loading } = useContactForm();
  const { trackContactForm, trackFormSubmit } = useGA4();
  const [formValues, setFormValues] = useState<FormData>({
    fullName: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Fallback data that matches the design exactly
  const fallbackData = {
    title: 'Contact formulier',
    description: 'Vul het formulier in en we komen zo snel mogelijk bij u terug.',
    fullNameLabel: 'Naam',
    emailLabel: 'Emailadres',
    messageLabel: 'Uw Bericht',
    submitButtonText: 'Verzenden',
    successMessage: 'Bedankt voor je bericht! We nemen zo snel mogelijk contact met je op.',
    errorMessage: 'Er is iets misgegaan. Probeer het opnieuw of neem contact op via telefoon.'
  };

  const data = formData || fallbackData;

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(`${getApiBaseUrl()}/contact-submission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formValues),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        
        // Track successful form submission
        trackContactForm('form', true);
        trackFormSubmit('contact_form', true);
        
        // Reset form after successful submission
        setTimeout(() => {
          setFormValues({
            fullName: '',
            email: '',
            message: ''
          });
          setSubmitStatus('idle');
        }, 5000); // Show success message longer
      } else {
        console.error('Form submission failed:', result);
        setSubmitStatus('error');
        
        // Track failed form submission
        trackContactForm('form', false);
        trackFormSubmit('contact_form', false);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      
      // Track failed form submission due to error
      trackContactForm('form', false);
      trackFormSubmit('contact_form', false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="contact-form-loading">
        Laden...
      </div>
    );
  }

  return (
    <div className="contact-form-wrapper-div">
      {/* Title & Description from Strapi */}
      {data.title && (
        <h2 className="contact-form-title" style={{ textAlign: 'center', marginBottom: '12px' }}>
          {data.title}
        </h2>
      )}
      {data.description && (
        <p className="contact-form-description">
          {data.description}
        </p>
      )}

      <form onSubmit={handleSubmit} className="contact-form-element">
        {/* Full Name Field */}
        <div className="contact-form-field">
          <label htmlFor="fullName" className="contact-form-label">
            {data.fullNameLabel}
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formValues.fullName}
            onChange={(e) => handleInputChange('fullName', e.target.value)}
            className="contact-form-input"
            required
          />
        </div>

        {/* Email Field */}
        <div className="contact-form-field">
          <label htmlFor="email" className="contact-form-label">
            {data.emailLabel}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formValues.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="contact-form-input"
            required
          />
        </div>

        {/* Message Field */}
        <div className="contact-form-field">
          <label htmlFor="message" className="contact-form-label">
            {data.messageLabel}
          </label>
          <textarea
            id="message"
            name="message"
            value={formValues.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            rows={4}
            className="contact-form-textarea"
            required
          />
        </div>

        {/* Submit Button */}
        <div className="contact-form-submit-wrapper" style={{ display: 'flex', justifyContent: 'center' }}>
          <SlotMachineButton
            text={data.submitButtonText}
            icon={<ArrowRight size={16} />}
            href="#"
            variant="tertiary"
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.preventDefault();
              handleSubmit(e as any);
            }}
          />
        </div>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div className="contact-form-success-message">
            {data.successMessage}
          </div>
        )}
        
        {submitStatus === 'error' && (
          <div className="contact-form-error-message">
            {data.errorMessage}
          </div>
        )}
      </form>
    </div>
  );
} 